import {
  Field,
  Label as HeadlessLabel,
  Input,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Combobox,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaAlignCenter,
  FaAlignLeft,
  FaAlignRight,
  FaEye,
  FaEyeSlash,
  FaGripLines,
  FaItalic,
  FaPlus,
  FaTextHeight,
  FaTrash,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  addStat,
  removeStat,
  reorderStat,
  setDescription,
  setDescriptionVisibility,
  setTitle,
  setTitleVisibility,
  updateLabelStyle,
  updateStat,
  updateStatStyle,
  setStatsSeparator,
} from "../store/labelsSlice";

// Tooltip Import
import { Tooltip } from "react-tooltip";

// Tiptap imports
import { Color } from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

// Dnd-kit imports
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Translation Import
import { useTranslation } from "react-i18next";

// Router imports
import { EditorPreviewRef } from "../components/EditorPreview";
import { useAddToCart } from '../utils/useAddToCart'; // Chemin re-vérifié, sensible à la casse
import { HiShoppingCart } from 'react-icons/hi'; // Assurer l'import

// --- Available Fonts ---
// Default font configuration
const DEFAULT_FONT = {
  label: "Oswald",
  value: "'Oswald', sans-serif"
};

const AVAILABLE_FONTS = [
  { label: "ABYS", value: "'ABYS', sans-serif" },
  { label: "LOVELO", value: "'LOVELO', sans-serif" },
  { label: "Abril Fatface", value: "'Abril Fatface', serif" },
  { label: "Alegreya", value: "'Alegreya', serif" },
  { label: "Alegreya Sans", value: "'Alegreya Sans', sans-serif" },
  { label: "Aller", value: "'Aller', sans-serif" },
  { label: "Anonymous Pro", value: "'Anonymous Pro', monospace" },
  { label: "Apfel Grotezk", value: "'Apfel Grotezk', sans-serif" },
  { label: "Apfel Grotezk Brukt", value: "'Apfel Grotezk Brukt', sans-serif" },
  { label: "Archivo Black", value: "'Archivo Black', sans-serif" },
  { label: "Archivo Narrow", value: "'Archivo Narrow', sans-serif" },
  { label: "Arimo", value: "'Arimo', sans-serif" },
  { label: "Arvo", value: "'Arvo', serif" },
  { label: "Bangers", value: "'Bangers', cursive" },
  { label: "B612", value: "'B612', sans-serif" },
  { label: "Bad Script", value: "'Bad Script', cursive" },
  { label: "Bebas Neue", value: "'Bebas Neue', sans-serif" },
  { label: "Bernier", value: "'Bernier', serif" },
  { label: "Bernier Shade", value: "'Bernier Shade', serif" },
  { label: "BioRhyme", value: "'BioRhyme', serif" },
  { label: "Blackout Midnight", value: "'Blackout Midnight', sans-serif" },
  { label: "Blackout Sunrise", value: "'Blackout Sunrise', sans-serif" },
  { label: "Blackout Two AM", value: "'Blackout Two AM', sans-serif" },
  { label: "Budmo Jiggler", value: "'Budmo Jiggler', cursive" },
  { label: "Bungee", value: "'Bungee', sans-serif" },
  { label: "Bungee Inline", value: "'Bungee Inline', sans-serif" },
  { label: "Bungee Outline", value: "'Bungee Outline', sans-serif" },
  { label: "Bungee Shade", value: "'Bungee Shade', sans-serif" },
  { label: "Cabin", value: "'Cabin', sans-serif" },
  { label: "Cardo", value: "'Cardo', serif" },
  { label: "Cheque", value: "'Cheque', serif" },
  { label: "Chewy", value: "'Chewy', cursive" },
  { label: "Chivo", value: "'Chivo', sans-serif" },
  { label: "Chunk", value: "'Chunk', serif" },
  { label: "Concert One", value: "'Concert One', cursive" },
  { label: "Cormorant", value: "'Cormorant', serif" },
  { label: "Crimson Text", value: "'Crimson Text', serif" },
  { label: "Dancing Script", value: "'Dancing Script', cursive" },
  { label: "Domine", value: "'Domine', serif" },
  { label: "Eczar", value: "'Eczar', serif" },
  { label: "Edo", value: "'Edo', cursive" },
  { label: "Faster One", value: "'Faster One', cursive" },
  { label: "Fira Sans", value: "'Fira Sans', sans-serif" },
  { label: "Frank Ruhl Libre", value: "'Frank Ruhl Libre', serif" },
  { label: "Fredericka the Great", value: "'Fredericka the Great', cursive" },
  { label: "Fruktur", value: "'Fruktur', cursive" },
  { label: "Gagalin", value: "'Gagalin', cursive" },
  { label: "Germania One", value: "'Germania One', sans-serif" },
  {
    label: "Glacial Indifference",
    value: "'Glacial Indifference', sans-serif",
  },
  { label: "Godoia", value: "'Godoia', serif" },
  { label: "Godoia Deco", value: "'Godoia Deco', serif" },
  { label: "Goudy Bookletter 1911", value: "'Goudy Bookletter 1911', serif" },
  { label: "Graduate", value: "'Graduate', serif" },
  { label: "Guerrilla", value: "'Guerrilla', cursive" },
  { label: "IBM Plex Sans", value: "'IBM Plex Sans', sans-serif" },
  { label: "Inconsolata", value: "'Inconsolata', monospace" },
  { label: "Inknut Antiqua", value: "'Inknut Antiqua', serif" },
  { label: "Inter", value: "'Inter', sans-serif" },
  { label: "Josefin Sans", value: "'Josefin Sans', sans-serif" },
  { label: "Karla", value: "'Karla', sans-serif" },
  { label: "Knewave", value: "'Knewave', cursive" },
  { label: "Kontanter", value: "'Kontanter', sans-serif" },
  { label: "Lato", value: "'Lato', sans-serif" },
  { label: "League Gothic", value: "'League Gothic', sans-serif" },
  {
    label: "League Gothic Condensed",
    value: "'League Gothic Condensed', sans-serif",
  },
  { label: "League Mono", value: "'League Mono', monospace" },
  { label: "League Script", value: "'League Script', cursive" },
  { label: "League Spartan", value: "'League Spartan', sans-serif" },
  { label: "Libre Baskerville", value: "'Libre Baskerville', serif" },
  { label: "Libre Franklin", value: "'Libre Franklin', sans-serif" },
  { label: "Lora", value: "'Lora', serif" },
  { label: "Lot", value: "'Lot', sans-serif" },
  { label: "Lovelo", value: "'Lovelo', sans-serif" },
  { label: "Merriweather", value: "'Merriweather', serif" },
  { label: "Messapia", value: "'Messapia', serif" },
  { label: "Modak", value: "'Modak', cursive" },
  { label: "Monoton", value: "'Monoton', cursive" },
  { label: "Montserrat", value: "'Montserrat', sans-serif" },
  { label: "Neuton", value: "'Neuton', serif" },
  { label: "New Rocker", value: "'New Rocker', cursive" },
  { label: "Norwester", value: "'Norwester', sans-serif" },
  { label: "Nunito Sans", value: "'Nunito Sans', sans-serif" },
  { label: "Open Sans", value: "'Open Sans', sans-serif" },
  { label: "Orbitron", value: "'Orbitron', sans-serif" },
  { label: "Oswald", value: "'Oswald', sans-serif" },
  { label: "Peace Sans", value: "'Peace Sans', sans-serif" },
  { label: "Perfograma", value: "'Perfograma', monospace" },
  { label: "Permanent Marker", value: "'Permanent Marker', cursive" },
  { label: "Pirata One", value: "'Pirata One', cursive" },
  { label: "Playfair Display", value: "'Playfair Display', serif" },
  { label: "Playlist", value: "'Playlist', cursive" },
  { label: "Playlist Caps", value: "'Playlist Caps', sans-serif" },
  { label: "Poppins", value: "'Poppins', sans-serif" },
  { label: "Proza Libre", value: "'Proza Libre', sans-serif" },
  { label: "PT Sans", value: "'PT Sans', sans-serif" },
  { label: "PT Serif", value: "'PT Serif', serif" },
  { label: "Rakkas", value: "'Rakkas', cursive" },
  { label: "Raleway", value: "'Raleway', sans-serif" },
  { label: "Repekt", value: "'Repekt', cursive" },
  { label: "Ribes", value: "'Ribes', cursive" },
  { label: "Roboto", value: "'Roboto', sans-serif" },
  { label: "Roboto Slab", value: "'Roboto Slab', serif" },
  { label: "Rozha One", value: "'Rozha One', serif" },
  { label: "Rubik", value: "'Rubik', sans-serif" },
  { label: "Rubik Mono One", value: "'Rubik Mono One', sans-serif" },
  { label: "Sacramento", value: "'Sacramento', cursive" },
  { label: "Selima", value: "'Selima', cursive" },
  { label: "Shrikhand", value: "'Shrikhand', cursive" },
  { label: "Sifonn", value: "'Sifonn', sans-serif" },
  { label: "Sifonn Outline", value: "'Sifonn Outline', sans-serif" },
  { label: "Sigmar One", value: "'Sigmar One', cursive" },
  { label: "Solena", value: "'Solena', cursive" },
  { label: "Source Sans Pro", value: "'Source Sans Pro', sans-serif" },
  { label: "Source Serif Pro", value: "'Source Serif Pro', serif" },
  { label: "Space Mono", value: "'Space Mono', monospace" },
  { label: "Spartan", value: "'Spartan', sans-serif" },
  { label: "Spectral", value: "'Spectral', serif" },
  { label: "Sunday", value: "'Sunday', cursive" },
  { label: "Tenor Sans", value: "'Tenor Sans', sans-serif" },
  {
    label: "Typeface Claire-Obscure",
    value: "'Typeface Claire-Obscure', sans-serif",
  },
  { label: "Typeface Pleine", value: "'Typeface Pleine', sans-serif" },
  { label: "Ultra", value: "'Ultra', serif" },
  { label: "UnifrakturCook", value: "'UnifrakturCook', cursive" },
  { label: "UnifrakturMaguntia", value: "'UnifrakturMaguntia', cursive" },
  { label: "Varela", value: "'Varela', sans-serif" },
  { label: "Varela Round", value: "'Varela Round', sans-serif" },
  { label: "Vollkorn", value: "'Vollkorn', serif" },
  { label: "Work Sans", value: "'Work Sans', sans-serif" },
  { label: "Yatra One", value: "'Yatra One', cursive" },
];

// Move Oswald to the beginning of the array
AVAILABLE_FONTS.sort((a, b) => {
  if (a.label === DEFAULT_FONT.label) return -1;
  if (b.label === DEFAULT_FONT.label) return 1;
  return a.label.localeCompare(b.label);
});

// --- Available Font Weights ---
const AVAILABLE_FONT_WEIGHTS = [
  { name: "light", value: 300 },
  { name: "normal", value: 400 },
  { name: "medium", value: 500 },
  { name: "bold", value: 600 },
  { name: "extrabold", value: 700 },
  { name: "black", value: 800 },
];

// --- Stat Default Color Fallback ---
const DEFAULT_STAT_COLOR = "#333333"; // Kept for logic, Tailwind for styling

// --- BASE STYLES FOR UI ELEMENTS ---
const baseInputClasses =
  "block w-full min-w-0 flex-1 rounded-md border-0 bg-neutral-800/80 py-1.5 px-3 text-sm shadow-sm ring-1 ring-inset ring-neutral-700/50 focus:ring-2 focus:ring-inset focus:ring-blue-500 text-white placeholder:text-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed";
const baseIconButtonClasses =
  "relative inline-flex items-center justify-center p-1.5 rounded text-neutral-400 hover:text-white hover:bg-neutral-700/80 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer";
const activeIconButtonClasses = "bg-blue-600 text-white hover:bg-blue-700";
const baseDropdownButtonClasses =
  "relative w-full min-w-0 flex-1 cursor-default rounded-md bg-neutral-800/80 py-1.5 pl-3 pr-10 text-left text-white shadow-sm ring-1 ring-inset ring-neutral-700/50 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed";
const baseDropdownOptionsContainerClasses =
  "absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md bg-neutral-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm";
const baseDropdownOptionClasses = ({ active }: { active: boolean }) =>
  clsx(
    "relative cursor-default select-none py-2 pl-3 pr-9",
    active ? "bg-blue-600 text-white" : "text-neutral-200"
  );
const sectionContainerClasses =
  "space-y-3 rounded-lg border border-neutral-700/50 bg-neutral-800/40 p-2 max-w-md w-full mx-auto";

// --- États pour le collapse des blocs ---
// (Supprimé ici, voir plus bas dans le composant principal)

// --- SortableItem composant utilitaire pour DnD des stats ---
type SortableItemProps = {
  id: string;
  children: React.ReactNode;
};
const SortableItem: React.FC<SortableItemProps> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center space-x-2 bg-neutral-700/40 p-1.5 rounded-md border border-neutral-700/60"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab text-neutral-500 hover:text-neutral-300 px-1 py-1"
      >
        <FaGripLines className="w-4 h-4" />
      </div>
      <div className="flex-grow flex items-center gap-x-2">{children}</div>
    </div>
  );
};

// --- Simple Rich Text Editor Component (Title/Description) ---
type TextTransform = "none" | "uppercase" | "lowercase" | "capitalize";
interface SimpleEditorStyle {
  color: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: number;
  isItalic: boolean;
  textAlign: "left" | "center" | "right" | "justify";
  marginTop: number;
  marginBottom: number;
  textTransform: TextTransform;
}
interface SimpleEditorProps {
  identifier: "title" | "description";
  content: string;
  isVisible: boolean;
  style: SimpleEditorStyle;
}

const SimpleEditor: React.FC<SimpleEditorProps> = ({
  identifier,
  content,
  isVisible,
  style,
}) => {
  const dispatch = useDispatch();
  const editorRef = useRef<Editor | null>(null);
  // Add state and ref for the color picker popover within SimpleEditor
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const colorPickerPopoverRef = useRef<HTMLDivElement | null>(null);
  // --- AJOUT: Ref pour le debounce de la couleur ---
  const debounceColorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // Ajout pour la traduction
  const { t } = useTranslation();

  const selectedLayoutId = useSelector((state: RootState) => state.layout.selectedLayoutId);

  const dispatchStyleUpdate = useCallback(
    (updates: Partial<SimpleEditorStyle>) => {
      dispatch(updateLabelStyle({ labelType: identifier, updates }));
      // Synchronisation layout-13 : si textAlign change, on l'applique à l'autre champ
      if (
        selectedLayoutId === "layout-13" &&
        Object.prototype.hasOwnProperty.call(updates, "textAlign") &&
        ["left", "center", "right", "justify"].includes(updates.textAlign as string)
      ) {
        const otherLabel = identifier === "title" ? "description" : "title";
        dispatch(updateLabelStyle({ labelType: otherLabel, updates: { textAlign: updates.textAlign } }));
      }
    },
    [dispatch, identifier, selectedLayoutId]
  );
  const dispatchContentUpdate = useCallback(
    (htmlContent: string) => {
      if (identifier === "title") dispatch(setTitle(htmlContent));
      else dispatch(setDescription(htmlContent));
    },
    [dispatch, identifier]
  );
  const handleVisibilityToggle = useCallback(() => {
    if (identifier === "title") dispatch(setTitleVisibility(!isVisible));
    else dispatch(setDescriptionVisibility(!isVisible));
  }, [dispatch, identifier, isVisible]);

  const editorInstance = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          blockquote: false,
          codeBlock: false,
          horizontalRule: false,
          strike: false,
          code: false,
          bold: false,
          italic: {},
        }),
        TextStyle,
        Color.configure({ types: [TextStyle.name] }),
        FontFamily.configure({ 
          types: [TextStyle.name]
        }),
        TextAlign.configure({ types: ["paragraph"] }),
      ],
      content: content,
      onUpdate: ({ editor: updatedEditor }) => {
        const currentHTML = updatedEditor.getHTML();
        if (currentHTML !== content) dispatchContentUpdate(currentHTML);
      },
      editable: isVisible,
    },
    [isVisible]
  );

  useEffect(() => {
    editorRef.current = editorInstance;
    return () => {
      editorRef.current?.destroy();
      editorRef.current = null;
      // --- AJOUT: Nettoyer le timeout du debounce ---
      if (debounceColorTimeoutRef.current) {
        clearTimeout(debounceColorTimeoutRef.current);
      }
    };
  }, [editorInstance]);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && !editor.isDestroyed && editor.isEditable) {
      const editorHTML = editor.getHTML();
      if (content !== editorHTML && !editor.isFocused) {
        editor.commands.setContent(content, false);
      }
    }
  }, [content]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editor.isDestroyed || !editor.isEditable) return;
    let needsRun = false;
    const chain = editor.chain();
    const isEditorItalic = editor.isActive("italic");
    if (isEditorItalic !== style.isItalic) {
      chain.toggleItalic();
      needsRun = true;
    }
    const currentAlign =
      ["left", "center", "right", "justify"].find((align) =>
        editor.isActive({ textAlign: align })
      ) || "left";
    if (currentAlign !== style.textAlign) {
      chain.setTextAlign(style.textAlign);
      needsRun = true;
    }
    const currentFontFamily =
      editor.getAttributes("textStyle").fontFamily || "";
    if (currentFontFamily !== style.fontFamily) {
      // Sélectionner tout le texte avant d'appliquer la police
      chain.selectAll().setFontFamily(style.fontFamily);
      needsRun = true;
    }
    if (needsRun) {
      chain.run();
    }
  }, [style, editorInstance, identifier]);

  // NEW Effect for handling clicks outside the SimpleEditor color picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerPopoverRef.current &&
        !colorPickerPopoverRef.current.contains(event.target as Node)
      ) {
        // Check if the click target was the button that opens the picker
        const clickedButton = (event.target as Element).closest(
          `button[aria-label="Couleur du texte"][data-editor-id="${identifier}"]`
        );
        if (!clickedButton) {
          setIsColorPickerOpen(false); // Close picker if click is outside and not on its trigger button
        }
      }
    };
    if (isColorPickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isColorPickerOpen, identifier]); // Rerun when picker opens/closes or identifier changes

  // --- AJOUT: Fonction de mise à jour déboncée pour la couleur ---
  const debounceColorUpdate = useCallback(
    (newColor: string) => {
      if (debounceColorTimeoutRef.current) {
        clearTimeout(debounceColorTimeoutRef.current);
      }
      debounceColorTimeoutRef.current = setTimeout(() => {
        // On ne dispatch que si la couleur a réellement changé par rapport au style actuel
        // pour éviter des dispatches inutiles si l'utilisateur revient à la couleur initiale.
        if (newColor !== style.color) {
          dispatchStyleUpdate({ color: newColor });
        }
      }, 10); // Délai réduit à 10ms pour une réaction immédiate
    },
    [dispatchStyleUpdate, style.color]
  ); // Dépendances: la fonction de dispatch et la couleur actuelle

  if (!editorInstance) return null;

  const toggleButtonClasses = (active: boolean) =>
    clsx(baseIconButtonClasses, active && activeIconButtonClasses);
  const transformButtonClasses = (buttonTransform: TextTransform) =>
    clsx(
      baseIconButtonClasses,
      style.textTransform === buttonTransform && activeIconButtonClasses
    );
  const currentWeightName = t(
    `labels.weight_${
      AVAILABLE_FONT_WEIGHTS.find((fw) => fw.value === style.fontWeight)
        ?.name || "normal"
    }`
  );

  // ** CORRECTION: Tooltip application: Only on the final interactive element **
  return (
    <div className="space-y-3 mt-3">
      {/* Toolbar Container */}
      <div
        className={clsx(
          "rounded-md border border-neutral-700/50 bg-neutral-800/40 p-3 w-full max-w-full space-y-3"
        )}
      >
        {/* Ligne 1: Style du texte */}
        <div className="space-y-2 mb-4">
          <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide mb-3">
            {t('labels.text_style')}
          </label>
          <div className="flex flex-wrap items-center gap-2 w-full">
            {/* Visibilité */}
            <button
              onClick={handleVisibilityToggle}
              className={clsx(baseIconButtonClasses)}
              id={`tooltip-${identifier}-visibility`}
              data-tooltip-id={`tooltip-${identifier}-visibility`}
              data-tooltip-content={isVisible ? "Cacher" : "Afficher"}
            >
              {isVisible ? (
                <FaEye className="w-4 h-4" />
              ) : (
                <FaEyeSlash className="w-4 h-4" />
              )}
            </button>
            
            {/* Autres contrôles avec opacité si non visible */}
            <div
              className={clsx(
                "flex flex-wrap items-center gap-2",
                !isVisible && "opacity-60"
              )}
            >
              {/* Transform */}
              <button
                onClick={() => {
                  const newTransform = style.textTransform === "uppercase" ? "none" : "uppercase";
                  dispatchStyleUpdate({ textTransform: newTransform });
                }}
                disabled={!isVisible}
                className={clsx(
                  transformButtonClasses("uppercase"),
                  "rounded-md"
                )}
                id={`tooltip-${identifier}-uppercase`}
                data-tooltip-id={`tooltip-${identifier}-uppercase`}
                data-tooltip-content={t('labels.tooltip_uppercase')}
              >
                <FaTextHeight className="w-4 h-4" />
              </button>
              
              {/* Italic */}
              <button
                onClick={() => dispatchStyleUpdate({ isItalic: !style.isItalic })}
                disabled={!isVisible}
                className={toggleButtonClasses(style.isItalic)}
                id={`tooltip-${identifier}-italic`}
                data-tooltip-id={`tooltip-${identifier}-italic`}
                data-tooltip-content={t('labels.tooltip_italic')}
              >
                <FaItalic className="w-4 h-4" />
              </button>
              
              {/* Alignement */}
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  onClick={() => dispatchStyleUpdate({ textAlign: "left" })}
                  disabled={!isVisible}
                  className={clsx(
                    toggleButtonClasses(style.textAlign === "left"),
                    "rounded-l-md"
                  )}
                  id={`tooltip-${identifier}-align-left`}
                  data-tooltip-id={`tooltip-${identifier}-align-left`}
                  data-tooltip-content={t('labels.tooltip_align_left')}
                >
                  <FaAlignLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => dispatchStyleUpdate({ textAlign: "center" })}
                  disabled={!isVisible}
                  className={clsx(
                    toggleButtonClasses(style.textAlign === "center"),
                    "border-x border-neutral-600"
                  )}
                  id={`tooltip-${identifier}-align-center`}
                  data-tooltip-id={`tooltip-${identifier}-align-center`}
                  data-tooltip-content={t('labels.tooltip_align_center')}
                >
                  <FaAlignCenter className="w-4 h-4" />
                </button>
                <button
                  onClick={() => dispatchStyleUpdate({ textAlign: "right" })}
                  disabled={!isVisible}
                  className={clsx(
                    toggleButtonClasses(style.textAlign === "right"),
                    "rounded-r-md"
                  )}
                  id={`tooltip-${identifier}-align-right`}
                  data-tooltip-id={`tooltip-${identifier}-align-right`}
                  data-tooltip-content={t('labels.tooltip_align_right')}
                >
                  <FaAlignRight className="w-4 h-4" />
                </button>
              </div>
              
              {/* Couleur - Carré après alignement */}
              <div className="relative">
                <input
                  type="color"
                  value={style.color || "#ffffff"}
                  onChange={(e) => debounceColorUpdate(e.target.value)}
                  disabled={!isVisible}
                  className="h-8 w-8 cursor-pointer appearance-none border border-neutral-600 rounded-md bg-white/5 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  title="Couleur du texte"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Ligne 2: Typographie */}
        <div className={clsx("space-y-2 mb-4", !isVisible && "opacity-60 pointer-events-none")}>
          <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide mb-3">
            {t('labels.typography')}
          </label>
          
          {/* Ligne 1: Font Family et Weight */}
          <div className="flex gap-2 w-full">
            {/* Font Family */}
            <div className="flex-1 min-w-0">
              <Listbox
                value={style.fontFamily || DEFAULT_FONT.value}
                onChange={(value) => dispatchStyleUpdate({ fontFamily: value })}
                disabled={!isVisible}
              >
                <div className="relative w-full">
                  <ListboxButton className={clsx(baseDropdownButtonClasses, "py-1 text-xs")} style={{ height: 28 }}>
                    <span className="block truncate text-xs">
                      {AVAILABLE_FONTS.find(
                        (f) => f.value === (style.fontFamily || DEFAULT_FONT.value)
                      )?.label || DEFAULT_FONT.label}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
                      <ChevronDownIcon className="h-3 w-3 text-neutral-400" />
                    </span>
                  </ListboxButton>
                  <ListboxOptions className={baseDropdownOptionsContainerClasses}>
                    {AVAILABLE_FONTS.map((font) => (
                      <ListboxOption
                        key={font.value}
                        className={baseDropdownOptionClasses}
                        value={font.value}
                      >
                        <>
                          <span
                            className="block truncate text-xs"
                            style={{ fontFamily: font.value }}
                          >
                            {font.label}
                          </span>
                        </>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
            
            {/* Font Weight */}
            <div className="w-28 flex-shrink-0">
              <Listbox
                value={style.fontWeight}
                onChange={(value) => dispatchStyleUpdate({ fontWeight: value })}
                disabled={!isVisible}
              >
                <div className="relative w-full">
                  <ListboxButton className={clsx(baseDropdownButtonClasses, "py-1 text-xs")} style={{ height: 28 }}>
                    <span className="block truncate text-xs">
                      {currentWeightName}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
                      <ChevronDownIcon className="h-3 w-3 text-neutral-400" />
                    </span>
                  </ListboxButton>
                  <ListboxOptions className={baseDropdownOptionsContainerClasses}>
                    {AVAILABLE_FONT_WEIGHTS.map((fw) => (
                      <ListboxOption
                        key={fw.value}
                        className={baseDropdownOptionClasses}
                        value={fw.value}
                      >
                        <>
                          <span className="block truncate text-xs">
                            {t(`labels.weight_${fw.name}`)}
                          </span>
                        </>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
          </div>
          
          {/* Ligne 2: Taille de police */}
          <div className="flex items-center w-full">
            <div className="flex items-center">
              <span className="text-xs text-neutral-400 mr-2 whitespace-nowrap">{t('labels.size')}:</span>
              <div className="flex items-center rounded-md bg-neutral-800/80 ring-1 ring-inset ring-neutral-700/50 shadow-sm">
                <button
                  type="button"
                  onClick={() => dispatchStyleUpdate({ fontSize: Math.max(8, style.fontSize - 1) })}
                  disabled={!isVisible || style.fontSize <= 8}
                  className="px-1.5 py-1 text-sm font-bold text-white bg-transparent rounded-l-md hover:bg-neutral-700 disabled:opacity-40 focus:outline-none"
                  aria-label="Diminuer la taille"
                  style={{ minWidth: 28 }}
                >
                  –
                </button>
                <Combobox value={style.fontSize} onChange={v => dispatchStyleUpdate({ fontSize: Number(v) })} disabled={!isVisible}>
                  <div className="relative w-12">
                    <Combobox.Input
                      className="w-full text-xs text-center border-y border-neutral-700 bg-neutral-900 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      style={{ borderLeft: 'none', borderRight: 'none', borderRadius: 0, height: 28 }}
                      displayValue={(v: number) => (typeof v === 'number' ? v.toString() : '')}
                      onChange={e => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 8 && val <= 120) {
                          dispatchStyleUpdate({ fontSize: val });
                        } else if (e.target.value === "") {
                          dispatchStyleUpdate({ fontSize: 8 });
                        }
                      }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Taille"
                      aria-label="Taille de police"
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-0.5">
                      <ChevronDownIcon className="h-3 w-3 text-neutral-400" />
                    </Combobox.Button>
                    <Combobox.Options className="absolute z-30 mt-1 max-h-56 w-12 overflow-auto rounded-md bg-neutral-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {[8,9,10,11,12,14,16,18,20,24,28,32,36,48,60,72,96,120].map((size) => (
                        <Combobox.Option key={size} value={size} className={({ active }) => clsx("cursor-default select-none py-1 px-1 text-xs text-center", active ? "bg-blue-600 text-white" : "text-neutral-200") }>
                          {size}
                        </Combobox.Option>
                      ))}
                    </Combobox.Options>
                  </div>
                </Combobox>
                <button
                  type="button"
                  onClick={() => dispatchStyleUpdate({ fontSize: Math.min(120, style.fontSize + 1) })}
                  disabled={!isVisible || style.fontSize >= 120}
                  className="px-1.5 py-1 text-sm font-bold text-white bg-transparent rounded-r-md hover:bg-neutral-700 disabled:opacity-40 focus:outline-none"
                  aria-label="Augmenter la taille"
                  style={{ minWidth: 28 }}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Ligne 3: Espacement */}
        <div className={clsx("space-y-2 mb-4", !isVisible && "opacity-60 pointer-events-none")}>
          <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide mb-3">
            {t('labels.spacing')}
          </label>
          
          {/* Marges - En version compacte */}
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            {/* Marge haute */}
            <div className="flex items-center">
              <span className="text-xs text-neutral-400 mr-1 whitespace-nowrap">{t('labels.top')}:</span>
              <div className="flex items-center rounded-md bg-neutral-800/80 ring-1 ring-inset ring-neutral-700/50 shadow-sm">
                <button
                  type="button"
                  onClick={() => dispatchStyleUpdate({ marginTop: Math.max(-50, style.marginTop - 1) })}
                  disabled={!isVisible || style.marginTop <= -50}
                  className="px-1.5 py-1 text-sm font-bold text-white bg-transparent rounded-l-md hover:bg-neutral-700 disabled:opacity-40 focus:outline-none"
                  aria-label="Diminuer la marge haute"
                  style={{ minWidth: 28 }}
                >
                  –
                </button>
                <Combobox value={style.marginTop} onChange={v => dispatchStyleUpdate({ marginTop: Number(v) })} disabled={!isVisible}>
                  <div className="relative w-10">
                    <Combobox.Input
                      className="w-10 text-xs text-center border-y border-neutral-700 bg-neutral-900 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      style={{ borderLeft: 'none', borderRight: 'none', borderRadius: 0, height: 28 }}
                      displayValue={(v: number) => (typeof v === 'number' ? v.toString() : '')}
                      onChange={e => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= -50 && val <= 100) {
                          dispatchStyleUpdate({ marginTop: val });
                        } else if (e.target.value === "") {
                          dispatchStyleUpdate({ marginTop: 0 });
                        }
                      }}
                      inputMode="numeric"
                      pattern="[0-9-]*"
                      min="-50"
                      max="100"
                      aria-label="Marge haute"
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-0.5">
                      <ChevronDownIcon className="h-3 w-3 text-neutral-400" />
                    </Combobox.Button>
                    <Combobox.Options className="absolute z-30 mt-1 max-h-56 w-10 overflow-auto rounded-md bg-neutral-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {[-50, -25, 0, 8, 16, 24, 32, 48, 64, 100].map((size) => (
                        <Combobox.Option key={size} value={size} className={({ active }) => clsx("cursor-default select-none py-1 px-1 text-xs text-center", active ? "bg-blue-600 text-white" : "text-neutral-200") }>
                          {size}
                        </Combobox.Option>
                      ))}
                    </Combobox.Options>
                  </div>
                </Combobox>
                <button
                  type="button"
                  onClick={() => dispatchStyleUpdate({ marginTop: Math.min(100, style.marginTop + 1) })}
                  disabled={!isVisible || style.marginTop >= 100}
                  className="px-1.5 py-1 text-sm font-bold text-white bg-transparent rounded-r-md hover:bg-neutral-700 disabled:opacity-40 focus:outline-none"
                  aria-label="Augmenter la marge haute"
                  style={{ minWidth: 28 }}
                >
                  +
                </button>
              </div>
            </div>
            
            {/* Marge basse */}
            <div className="flex items-center">
              <span className="text-xs text-neutral-400 mr-1 whitespace-nowrap">{t('labels.bottom')}:</span>
              <div className="flex items-center rounded-md bg-neutral-800/80 ring-1 ring-inset ring-neutral-700/50 shadow-sm">
                <button
                  type="button"
                  onClick={() => dispatchStyleUpdate({ marginBottom: Math.max(-50, style.marginBottom - 1) })}
                  disabled={!isVisible || style.marginBottom <= -50}
                  className="px-1.5 py-1 text-sm font-bold text-white bg-transparent rounded-l-md hover:bg-neutral-700 disabled:opacity-40 focus:outline-none"
                  aria-label="Diminuer la marge basse"
                  style={{ minWidth: 28 }}
                >
                  –
                </button>
                <Combobox value={style.marginBottom} onChange={v => dispatchStyleUpdate({ marginBottom: Number(v) })} disabled={!isVisible}>
                  <div className="relative w-10">
                    <Combobox.Input
                      className="w-10 text-xs text-center border-y border-neutral-700 bg-neutral-900 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      style={{ borderLeft: 'none', borderRight: 'none', borderRadius: 0, height: 28 }}
                      displayValue={(v: number) => (typeof v === 'number' ? v.toString() : '')}
                      onChange={e => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= -50 && val <= 100) {
                          dispatchStyleUpdate({ marginBottom: val });
                        } else if (e.target.value === "") {
                          dispatchStyleUpdate({ marginBottom: 0 });
                        }
                      }}
                      inputMode="numeric"
                      pattern="[0-9-]*"
                      min="-50"
                      max="100"
                      aria-label="Marge basse"
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-0.5">
                      <ChevronDownIcon className="h-3 w-3 text-neutral-400" />
                    </Combobox.Button>
                    <Combobox.Options className="absolute z-30 mt-1 max-h-56 w-10 overflow-auto rounded-md bg-neutral-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {[-50, -25, 0, 8, 16, 24, 32, 48, 64, 100].map((size) => (
                        <Combobox.Option key={size} value={size} className={({ active }) => clsx("cursor-default select-none py-1 px-1 text-xs text-center", active ? "bg-blue-600 text-white" : "text-neutral-200") }>
                          {size}
                        </Combobox.Option>
                      ))}
                    </Combobox.Options>
                  </div>
                </Combobox>
                <button
                  type="button"
                  onClick={() => dispatchStyleUpdate({ marginBottom: Math.min(100, style.marginBottom + 1) })}
                  disabled={!isVisible || style.marginBottom >= 100}
                  className="px-1.5 py-1 text-sm font-bold text-white bg-transparent rounded-r-md hover:bg-neutral-700 disabled:opacity-40 focus:outline-none"
                  aria-label="Augmenter la marge basse"
                  style={{ minWidth: 28 }}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Éditeur de texte */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide mb-3">
          {t('labels.your_text')}
        </label>
        {/* Editeur Tiptap */}
        <div
          className={clsx(
            "block w-full rounded-md border border-neutral-700/50 bg-neutral-900/50 p-3 text-sm text-white focus-within:ring-1 focus-within:ring-blue-500",
            !isVisible && "opacity-50 bg-neutral-800/30 cursor-not-allowed"
          )}
        >
          <EditorContent
            editor={editorInstance}
            style={{
              color: "#FFFFFF !important",
              fontWeight: style.fontWeight,
              fontStyle: style.isItalic ? "italic" : "normal",
            }}
            className="outline-none prose prose-invert prose-sm max-w-none caret-white"
          />
        </div>
      </div>

      {/* Tooltip Components for SimpleEditor */}
      <Tooltip
        id={`tooltip-${identifier}-visibility`}
        place="top"
        opacity={1}
        style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }}
      />
      <Tooltip
        id={`tooltip-${identifier}-uppercase`}
        place="top"
        opacity={1}
        style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }}
      />
      <Tooltip
        id={`tooltip-${identifier}-italic`}
        place="top"
        opacity={1}
        style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }}
      />
      <Tooltip
        id={`tooltip-${identifier}-align-left`}
        place="top"
        opacity={1}
        style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }}
      />
      <Tooltip
        id={`tooltip-${identifier}-align-center`}
        place="top"
        opacity={1}
        style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }}
      />
      <Tooltip
        id={`tooltip-${identifier}-align-right`}
        place="top"
        opacity={1}
        style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }}
      />
    </div>
  );
};

// === NEW Memoized Stat Row Content Component ===
// Type for a single stat, derived from the Redux state structure
type StatItem = ReturnType<(state: RootState) => typeof state.labels.stats>[0];

interface StatRowContentProps {
  stat: StatItem; // Vient maintenant de l'état local du parent `localStats`
  index: number;
  handleLocalLabelChange: (index: number, value: string) => void; // Modifie localStats
  handleLocalValueChange: (index: number, value: string) => void; // Modifie localStats
  handleLocalStyleChange: (
    index: number,
    updates: Partial<StatItem["style"]>
  ) => void;
  handleSaveChanges: (index: number) => void; // Déclenche la sauvegarde Redux depuis localStats
  handleRemoveStat: (index: number) => void;
  debounceStyleUpdate: (
    index: number,
    changedUpdates: Partial<StatItem["style"]>
  ) => void;
}

const StatRowContent = React.memo<StatRowContentProps>(
  ({
    stat, // Reçoit la stat de localStats
    index,
    handleLocalLabelChange,
    handleLocalValueChange,
    handleLocalStyleChange,
    handleSaveChanges,
    handleRemoveStat,
    debounceStyleUpdate,
  }) => {
    // Garder les états locaux pour les inputs avec debounce (taille, couleur)
    const [localInputColor, setLocalInputColor] = useState(
      stat.style?.color || DEFAULT_STAT_COLOR
    );
    const localColorDebounceTimeoutRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);
    const [localInputFontSize, setLocalInputFontSize] = useState<string>(
      (stat.style?.fontSize || 16).toString()
    );
    const localSizeDebounceTimeoutRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);

    // --- onChange appelle DIRECTEMENT les props du parent ---
    const onLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleLocalLabelChange(index, e.target.value); // Met à jour localStats DANS Labels
    };
    const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleLocalValueChange(index, e.target.value); // Met à jour localStats DANS Labels
    };

    // Handlers de style restent inchangés
    const onFontChange = (value: string) => {
      handleLocalStyleChange(index, { fontFamily: value });
      debounceStyleUpdate(index, { fontFamily: value });
    };
    const handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newColor = e.target.value;
      setLocalInputColor(newColor);
      if (localColorDebounceTimeoutRef.current) {
        clearTimeout(localColorDebounceTimeoutRef.current);
      }
      localColorDebounceTimeoutRef.current = setTimeout(() => {
        handleLocalStyleChange(index, { color: newColor });
        debounceStyleUpdate(index, { color: newColor });
      }, 10); // 10ms debounce pour une réactivité maximale sans ralentir le UI
    };
    const handleSizeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      setLocalInputFontSize(rawValue);
      if (localSizeDebounceTimeoutRef.current) {
        clearTimeout(localSizeDebounceTimeoutRef.current);
      }
      localSizeDebounceTimeoutRef.current = setTimeout(() => {
        const newSize = parseInt(rawValue, 10);
        if (!isNaN(newSize) && newSize >= 8 && newSize <= 48) {
          handleLocalStyleChange(index, { fontSize: newSize });
          debounceStyleUpdate(index, { fontSize: newSize });
        } else {
          setLocalInputFontSize((stat.style?.fontSize || 16).toString());
        }
      }, 400);
    };

    // --- onBlur appelle le handler du parent ---
    const onBlur = () => handleSaveChanges(index);
    const onRemove = () => handleRemoveStat(index);

    return (
      <div className="flex flex-col gap-y-2 w-full max-w-full">
        {/* Row 1: Label & Value Inputs */}
        <div className="flex flex-wrap gap-x-2 w-full max-w-full">
          <div className="flex-1 min-w-[100px]">
            <Input
              placeholder="Libellé"
              value={stat.label}
              onChange={onLabelChange}
              onBlur={onBlur}
              className={clsx(
                baseInputClasses,
                "text-xs",
                "w-full min-w-0 flex-1",
                "text-white"
              )}
            />
          </div>
          <div className="flex-1 min-w-[80px]">
            <Input
              placeholder="Valeur"
              value={stat.value}
              onChange={onValueChange}
              onBlur={onBlur}
              className={clsx(
                baseInputClasses,
                "text-xs",
                "w-full min-w-0 flex-1",
                "text-white"
              )}
            />
          </div>
        </div>
        {/* Row 2: Controls */}
        <div className="flex items-center flex-wrap gap-x-2 gap-y-2 w-full max-w-full relative">
          {/* Font */}
          <div className="flex-1 min-w-[120px]">
            <Listbox
              value={stat.style?.fontFamily || DEFAULT_FONT.value}
              onChange={onFontChange}
            >
              <div className="relative">
                <ListboxButton
                  className={clsx(
                    baseDropdownButtonClasses,
                    "w-full min-w-0 flex-1 py-1 text-xs pl-2 pr-5"
                  )}
                >
                  <span className="block truncate text-neutral-300">
                    {AVAILABLE_FONTS.find(
                      (f) => f.value === (stat.style?.fontFamily || DEFAULT_FONT.value)
                    )?.label || DEFAULT_FONT.label}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
                    <ChevronDownIcon className="h-5 w-5 text-neutral-400" />
                  </span>
                </ListboxButton>
                <ListboxOptions className={baseDropdownOptionsContainerClasses}>
                  {AVAILABLE_FONTS.map((font) => (
                    <ListboxOption
                      key={font.value}
                      className={baseDropdownOptionClasses}
                      value={font.value}
                    >
                      <>
                        <span
                          className="block truncate"
                          style={{ fontFamily: font.value }}
                        >
                          {font.label}
                        </span>
                      </>
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>
          {/* Size */}
          <Input
            type="number"
            title="Taille police stat."
            aria-label="Taille police stat."
            min="8"
            max="48"
            value={localInputFontSize}
            onChange={handleSizeInputChange}
            className={clsx(
              baseInputClasses,
              "w-14 min-w-0 flex-shrink-0 text-xs text-center px-1 py-1"
            )}
          />
          {/* Color Button & Picker - Use props passed down */}
          <div className="relative flex-shrink-0">
            <input
              type="color"
              value={localInputColor}
              onChange={handleColorInputChange}
              className="h-8 w-8 cursor-pointer appearance-none border border-white/10 rounded-md bg-white/5 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
          </div>
          {/* Remove */}
          <button
            type="button"
            onClick={onRemove}
            className={clsx(
              baseIconButtonClasses,
              "text-neutral-500 hover:text-red-500",
              "ml-auto flex-shrink-0"
            )}
            id={`tooltip-stat-${index}-delete`}
            data-tooltip-id={`tooltip-stat-${index}-delete`}
            data-tooltip-content="Supprimer cette statistique"
          >
            <FaTrash className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }
);

// --- Composant principal des étiquettes ---
interface LabelsProps {
  editorPreviewRef: React.RefObject<EditorPreviewRef | null>;
}

const Labels: React.FC<LabelsProps> = ({ editorPreviewRef }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { title, description, stats, statsSeparator } = useSelector(
    (state: RootState) => state.labels
  );
  const selectedLayoutId = useSelector((state: RootState) => state.layout.selectedLayoutId);

  // Collapse states for each section
  const [titleCollapsed, setTitleCollapsed] = useState(false);
  const [descriptionCollapsed, setDescriptionCollapsed] = useState(true);
  const [statsCollapsed, setStatsCollapsed] = useState(true);

  // State: Local copy of stats for editing
  const [localStats, setLocalStats] = useState(() =>
    stats.map((stat) => ({ ...stat, style: { ...(stat.style || {}) } }))
  );
  // Ref for debounce timer
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Effect to sync localStats when Redux stats change
  useEffect(() => {
    setLocalStats(
      stats.map((stat) => ({ ...stat, style: { ...(stat.style || {}) } }))
    );
  }, [stats]);

  // --- Handlers for Local State Updates (passed to memoized component) ---
  const handleLocalLabelChange = useCallback((index: number, value: string) => {
    setLocalStats((current) => {
      const next = [...current];
      if (next[index]) next[index] = { ...next[index], label: value };
      return next;
    });
  }, []);

  const handleLocalValueChange = useCallback((index: number, value: string) => {
    setLocalStats((current) => {
      const next = [...current];
      if (next[index]) next[index] = { ...next[index], value: value };
      return next;
    });
  }, []);

  const handleLocalStyleChange = useCallback(
    (index: number, updates: Partial<StatItem["style"]>) => {
      setLocalStats((current) => {
        const next = [...current];
        if (next[index]) {
          const currentStyle = next[index].style || {};
          next[index] = {
            ...next[index],
            style: { ...currentStyle, ...updates },
          };
          // HACK : si c'est la première stat, propager le style sur toutes les autres
          if (index === 0) {
            for (let i = 1; i < next.length; i++) {
              next[i] = {
                ...next[i],
                style: { ...next[i].style, ...updates },
              };
            }
          }
        }
        return next;
      });
    },
    []
  );

  // --- THIS IS THE DEBOUNCE HANDLER FOR REDUX STYLE UPDATES ---
  const debounceStyleUpdate = useCallback(
    (index: number, changedUpdates: Partial<StatItem["style"]>) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        if (index === 0) {
          // Propager le style sur toutes les stats dans Redux
          for (let i = 0; i < localStats.length; i++) {
            dispatch(updateStatStyle({ index: i, updates: changedUpdates }));
          }
        } else {
          dispatch(updateStatStyle({ index, updates: changedUpdates }));
        }
      }, 800);
    },
    [dispatch, localStats]
  );

  // --- Handler for Saving Changes (on Blur) ---
  const handleSaveChanges = useCallback(
    (index: number) => {
      const local = localStats[index];
      const redux = stats[index];
      if (!local || !redux) return;

      // Save Label/Value if changed
      if (local.label !== redux.label || local.value !== redux.value) {
        console.log(
          `Dispatching label/value update on blur for index ${index}`
        );
        dispatch(updateStat({ index, label: local.label, value: local.value }));
      }

      // Save Non-Color Styles if changed
      const localStyleNoColor = { ...(local.style || {}) };
      delete localStyleNoColor.color;
      const reduxStyleNoColor = { ...(redux.style || {}) };
      delete reduxStyleNoColor.color;
      if (
        JSON.stringify(localStyleNoColor) !== JSON.stringify(reduxStyleNoColor)
      ) {
        console.log(
          `Dispatching non-color style update on blur for index ${index}`,
          localStyleNoColor
        );
        dispatch(updateStatStyle({ index, updates: localStyleNoColor }));
      }
    },
    [dispatch, stats, localStats]
  );

  // --- Handler for Removing Stat ---
  const handleRemoveStat = useCallback(
    (index: number) => {
      dispatch(removeStat(index));
    },
    [dispatch]
  );

  // --- DND Handlers (ensure localStats dependency is correct) ---
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active.id !== over?.id && over?.id) {
        // Find indices based on the current localStats order
        const oldIndex = localStats.findIndex(
          (_, i) => `stat-${i}` === active.id
        );
        const newIndex = localStats.findIndex(
          (_, i) => `stat-${i}` === over.id
        );
        if (oldIndex !== -1 && newIndex !== -1) {
          // Dispatch reorder action to Redux FIRST
          dispatch(reorderStat({ startIndex: oldIndex, endIndex: newIndex }));
          // THEN update local state optimistically OR rely on useEffect sync
          // Let's rely on useEffect sync based on `stats` prop from Redux for simplicity
        }
      }
    },
    [dispatch, localStats]
  ); // Keep localStats for findIndex

  const handleAddStat = () => {
    dispatch(addStat({ label: "Nouvelle statistique", value: "Valeur" }));
  };

  // Utilisation du hook useAddToCart pour harmoniser l'overlay
  const { handleAddToCart, isAddingToCart, cartOverlay } = useAddToCart(editorPreviewRef);

  return (
    <div className="w-full w-[700px] max-w-[700px] mx-auto px-4 space-y-6 text-white">
      {cartOverlay} {/* Added cartOverlay */}
      <div className="space-y-1">
        <h1 className="text-xl font-semibold font-sans text-white">
          {t("labels.title")}
        </h1>
        <p className="text-neutral-400 font-light text-sm">
          {t("labels.subtitle")}
        </p>
      </div>

      {/* Titre Section Collapsible */}
      <Field
        as="div"
        className={sectionContainerClasses}
      >
        <div
          className="flex items-center justify-between cursor-pointer select-none gap-0 hover:bg-neutral-700/20 transition-colors duration-200 rounded-md p-2 -m-2"
          onClick={() => setTitleCollapsed((prev) => !prev)}
        >
          <HeadlessLabel className="font-sans text-base font-medium text-white flex-1 items-center pointer-events-none">
            {t("labels.label_title")}
          </HeadlessLabel>
          <span
            className="ml-2 transition-transform pointer-events-none"
            style={{
              transform: titleCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
            }}
          >
            <ChevronDownIcon className="h-5 w-5 text-neutral-400" />
          </span>
        </div>
        {!titleCollapsed && (
          <SimpleEditor
            identifier="title"
            content={title.text}
            isVisible={title.isVisible}
            style={title.style}
          />
        )}
      </Field>

      {/* Description Section Collapsible */}
      <Field
        as="div"
        className={sectionContainerClasses}
      >
        <div
          className="flex items-center justify-between cursor-pointer select-none gap-0 hover:bg-neutral-700/20 transition-colors duration-200 rounded-md p-2 -m-2"
          onClick={() => setDescriptionCollapsed((prev) => !prev)}
        >
          <HeadlessLabel className="font-sans text-base font-medium text-white flex-1 items-center pointer-events-none">
            {t("labels.label_description")}
          </HeadlessLabel>
          <span
            className="ml-2 transition-transform pointer-events-none"
            style={{
              transform: descriptionCollapsed
                ? "rotate(-90deg)"
                : "rotate(0deg)",
            }}
          >
            <ChevronDownIcon className="h-5 w-5 text-neutral-400" />
          </span>
        </div>
        {!descriptionCollapsed && (
          <SimpleEditor
            identifier="description"
            content={description.text}
            isVisible={description.isVisible}
            style={description.style}
          />
        )}
      </Field>

      {/* Statistiques Section Collapsible */}
      <Field
        as="div"
        className={sectionContainerClasses}
      >
        <div
          className="flex items-center justify-between cursor-pointer select-none gap-0 hover:bg-neutral-700/20 transition-colors duration-200 rounded-md p-2 -m-2"
          onClick={() => setStatsCollapsed((prev) => !prev)}
        >
          <HeadlessLabel className="font-sans text-base font-medium text-white flex-1 items-center pointer-events-none">
            {t("labels.stats")}
          </HeadlessLabel>
          <span
            className="ml-2 transition-transform pointer-events-none"
            style={{
              transform: statsCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
            }}
          >
            <ChevronDownIcon className="h-5 w-5 text-neutral-400" />
          </span>
        </div>
        {!statsCollapsed && (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={localStats.map((_, index) => `stat-${index}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2 mt-3">
                  {localStats.map((localStat, index) => (
                    <SortableItem
                      key={`stat-${index}`}
                      id={`stat-${index}`}
                    >
                      <StatRowContent
                        stat={localStat}
                        index={index}
                        handleLocalLabelChange={handleLocalLabelChange}
                        handleLocalValueChange={handleLocalValueChange}
                        handleLocalStyleChange={handleLocalStyleChange}
                        handleSaveChanges={handleSaveChanges}
                        handleRemoveStat={handleRemoveStat}
                        debounceStyleUpdate={debounceStyleUpdate}
                      />
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            
            {/* Configuration du séparateur pour layout-13 */}
            {selectedLayoutId === "layout-13" && (
              <div className="mt-4 space-y-2">
                <label className="text-xs font-medium text-neutral-300 uppercase tracking-wide">
                  {t('labels.stats_separator', 'Séparateur')}
                </label>
                <div className="flex items-center space-x-2">
                  <Input
                    placeholder="•"
                    value={statsSeparator ?? ""}
                    onChange={(e) => dispatch(setStatsSeparator(e.target.value))}
                    className={clsx(
                      baseInputClasses,
                      "text-xs w-20 text-center"
                    )}
                    maxLength={3}
                  />
                  <span className="text-xs text-neutral-400">
                    {t('labels.stats_separator_help', 'Caractère affiché entre les statistiques')}
                  </span>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleAddStat}
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <FaPlus className="w-4 h-4" />
                {t("labels.add_stat")}
              </button>
            </div>
          </>
        )}
      </Field>

      {/* Bouton Ajouter au panier en bas, style identique à Overview */}
      <button
        onClick={handleAddToCart}
        disabled={isAddingToCart}
        className="w-full mt-6 flex justify-center items-center space-x-2 text-white py-2 rounded-sm text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-wait"
        type="button"
        style={{ backgroundColor: isAddingToCart ? '#FC4C02' : '#FC4C02', opacity: isAddingToCart ? 0.5 : 1 }}
        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d63a00')}
        onMouseOut={e => (e.currentTarget.style.backgroundColor = '#FC4C02')}
      >
        <HiShoppingCart className="w-4 h-4" />
        <span>{t('common.addToCart', 'Ajouter au panier')}</span>
      </button>
    </div>
  );
};

export default Labels;